import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import AdminLayout from '../../components/AdminLayout';
import api from '../../utils/api';
import { FileText, Users, DollarSign, Send, Eye, LogIn, Activity } from 'lucide-react';

const AdminActivity = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadActivityLogs();
  }, []);

  const loadActivityLogs = async () => {
    try {
      const response = await api.get('/activity');
      setLogs(response.data);
    } catch (error) {
      console.error('Failed to load activity logs', error);
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action) => {
    if (action.includes('application')) return <FileText className="h-4 w-4" />;
    if (action.includes('quote')) return <DollarSign className="h-4 w-4" />;
    if (action.includes('affiliate')) return <Users className="h-4 w-4" />;
    if (action.includes('sent')) return <Send className="h-4 w-4" />;
    if (action.includes('viewed')) return <Eye className="h-4 w-4" />;
    if (action.includes('login')) return <LogIn className="h-4 w-4" />;
    return <Activity className="h-4 w-4" />;
  };

  const getActionColor = (action) => {
    if (action.includes('created') || action.includes('submitted')) return 'text-green-600 bg-green-50';
    if (action.includes('updated')) return 'text-[#7B6FE8] bg-[#EEF0FF]';
    if (action.includes('deleted')) return 'text-red-600 bg-red-50';
    if (action.includes('sent')) return 'text-purple-600 bg-purple-50';
    if (action.includes('viewed')) return 'text-amber-600 bg-amber-50';
    return 'text-[#6B7280] bg-[#F7F8FA]';
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-400"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-black text-[#1A1A1A] uppercase tracking-wide" style={{fontFamily: "Barlow Condensed, sans-serif"}}>Activity Logs</h1>
          <p className="text-[#6B7280] mt-1">Audit trail of all system actions</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{logs.length} Activities</CardTitle>
          </CardHeader>
          <CardContent>
            {logs.length === 0 ? (
              <p className="text-[#9CA3AF] text-center py-8">No activity logs yet</p>
            ) : (
              <div className="space-y-3">
                {logs.map((log) => (
                  <div
                    key={log.id}
                    data-testid={`activity-log-${log.id}`}
                    className="border border-[#E5E7EB] rounded-lg p-4 flex items-start gap-4"
                  >
                    <div className={`p-2 rounded-lg ${getActionColor(log.action)}`}>
                      {getActionIcon(log.action)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="font-semibold text-[#1A1A1A]">
                          {log.action.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                        </h4>
                        <span className="text-sm text-[#9CA3AF]">
                          {new Date(log.timestamp).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-sm text-[#6B7280]">
                        {log.entity_type} • {log.entity_id.substring(0, 8)}
                      </p>
                      {log.user_email && (
                        <p className="text-sm text-[#9CA3AF] mt-1">By: {log.user_email}</p>
                      )}
                      {Object.keys(log.details || {}).length > 0 && (
                        <div className="mt-2 text-xs text-[#9CA3AF]">
                          {JSON.stringify(log.details, null, 2).length > 100
                            ? JSON.stringify(log.details).substring(0, 100) + '...'
                            : JSON.stringify(log.details, null, 2)}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminActivity;
